import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { FilterIcon } from '@seedlang/icons';
import { AppUI } from '@seedlang/state';
import { isBlank, isPresent } from '@seedlang/utils';
import SearchInput from 'components/form/search_input';
import { gaps } from 'components/layout/stack';
import Modal from 'components/modal';
import SegmentedControl from 'components/segmented_control';
import WorksheetFilterOptions from 'components/worksheet/worksheet_filter_options';
import WorksheetFilterTags from 'components/worksheet/worksheet_filter_tags';
import { isFunction, some } from 'lodash';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;
  padding: ${props => props.padding};
  justify-content: ${props => props.justifyContent};
`;

const SearchWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: ${gaps.sm};
  align-items: center;
  input {
    border-radius: 30px;
    height: 38px;
  }
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px 15px;
  background: ${props => props.background};
  border: none;
  border-radius: 30px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  height: 38px;
  
  &:hover {
    background: #f5f5f5;
  }
  
  i {
    font-size: 14px;
    margin: 2px;
  }
`;

const FilterWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #e8e8e8;
  border-radius: 24px;
  padding: 2px;
  height: 38px;
  min-width: fit-content;
  overflow: hidden;
`;

const FilterIconWrapper = styled.button`
  width: 38px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 22px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0 5px;
  position: relative;

  &:hover {
    background: #FFF;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  }


  .icon-wrapper {
    height: 20px;
  }

  svg {
    width: 20px;
  }

  &:hover svg {
    fill: #000;
  }
`;

const SearchIconWrapper = styled.button`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e8e8e8;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  margin-right: auto;

  &:hover {
    background: #f5f5f5;
  }

  i {
    font-size: 16px;
    color: #444;
  }

  &:hover i {
    color: #000;
  }
`;

const FilterCountBubble = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  background: #7D7D7D;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  z-index: 10;
`;

@observer
class WorksheetFilter extends React.Component {
  @observable showFilterOptions = false;
  @observable searchQuery = null;
  @observable expandSearch = false;
  onCloseModal = () => {
    this.showFilterOptions = false;
  }
  onCollapseSearch = () => {
    this.expandSearch = false;
  }

  @computed get userGroupFilteredTagCategories() {
    if (this.props.isExerciseFilter) {
      const exerciseTagIds = AppUI.cookieStore.exerciseTagIds;
      const exerciseProgressTags = AppUI.cookieStore.exerciseProgressTags;
      return this.props.allTagCategories?.filter(item =>
        item.tags.some(tag => exerciseTagIds?.split(',').includes(tag.id))
        || (item.slug === 'progress' && isPresent(exerciseProgressTags))
      );
    } else {
      const worksheetTagIds = AppUI.cookieStore[`worksheetTagIds${this.props.activeView}`];
      const worksheetProgressTags = AppUI.cookieStore[`worksheetProgressTags${this.props.activeView}`];
      return isPresent(this.props.allTagCategories)
        ? this.props.allTagCategories?.filter(item =>
          some(item.tags.map(tag => isPresent(worksheetTagIds) && worksheetTagIds?.split(',').includes(tag.id)))
          || (item.slug === 'progress' && isPresent(worksheetProgressTags))
        )
        : [];
    }
  }

  @computed get postName() {
    if (isPresent(this.props.routeName) && this.props.routeName.startsWith('podcast')) {
      return 'Podcast Episode';
    } if (isPresent(this.props.routeName) && this.props.routeName.startsWith('exercise')) {
      return 'Exercise';
    } return 'Post';
  }
  insertText = (text, caretPosition) => {
    let newValue;
    if (!caretPosition) {
      caretPosition = this.props.searchQuery ? this.props.searchQuery.length : 0;
    }
    if (this.props.searchQuery) {
      newValue = [this.props.searchQuery.slice(0, caretPosition), text, this.props.searchQuery.slice(caretPosition)].join('');
    } else {
      newValue = text;
    }
    this.props.onSearchQueryChange(newValue);
  }

  @computed get showFilterPlaceholder() {
    if (this.props.isExerciseFilter) {
      return isBlank(AppUI.cookieStore.exerciseTagIds) && (isBlank(AppUI.cookieStore.exerciseProgressTags) || !this.props.showProgressTag);
    } else {
      const worksheetTagIds = AppUI.cookieStore[`worksheetTagIds${this.props.activeView}`];
      const worksheetProgressTags = AppUI.cookieStore[`worksheetProgressTags${this.props.activeView}`];
      return isBlank(worksheetTagIds) && (isBlank(worksheetProgressTags) || !this.props.showProgressTag);
    }
  }

  @computed get hasFilterTags() {
    return this.activeFilterCount > 0;
  }

  @computed get activeFilterCount() {
    let count = 0;
    count += this.userGroupFilteredTagCategories.length;
    if (this.props.isExerciseFilter) {
      if (isPresent(AppUI.cookieStore.exerciseProgressTags)) {
        count += 1;
      }
    } else {
      const worksheetProgressTags = AppUI.cookieStore[`worksheetProgressTags${this.props.activeView}`];
      if (isPresent(worksheetProgressTags)) {
        count += 1;
      }
    }
    return count;
  }

  render() {
    return (
      <Wrapper
        className='worksheet-filter'
        hasSort={!!this.props.onToggleSort}
        padding={AppUI.layout.isMobile ? '0 5px' : '0'}
        justifyContent={AppUI.layout.isMobile ? 'flex-end' : 'flex-start'}
      >
        {
          this.showFilterOptions &&
            <Modal
              width={AppUI.layout.isMobile ? '85%' : '400px'}
              height={AppUI.layout.isMobile ? '85%' : '600px'}
              onCloseModal={this.onCloseModal}
              padding='0'
              title='Filters'
              contentShouldScroll
              background={AppUI.site.secondaryAccentColor || AppUI.site.accentColor || Theme.blue}
            >
              <WorksheetFilterOptions
                isExerciseFilter={this.props.isExerciseFilter}
                onUpdateFilter={this.props.onUpdateFilter}
                showProgressTag={this.props.showProgressTag}
                tagCategories={this.props.filteredTagCategories}
                activeView={this.props.activeView}
                {...this.props}
              />
            </Modal>
        }
        {
          AppUI.layout.isMobile && !this.expandSearch ?
            <SearchIconWrapper onClick={() => this.expandSearch = true}>
              <i className='fa fa-search' />
            </SearchIconWrapper>
          :
            <SearchWrapper>
              <SearchInput
                noBorder
                hideSpecialCharacters
                placeholder={this.props.isExerciseFilter ? 'Search for an Exercise Title' : 'Search for an Episode Title'}
                onChange={this.props.onSearchQueryChange}
                value={this.props.searchQuery}
                background='#FFF'
                insertText={this.insertText}
                hideCloseIcon={this.expandSearch}
              />
              {
                AppUI.layout.isMobile && this.expandSearch &&
                  <SearchIconWrapper onClick={this.onCollapseSearch}>
                    <i className='fa fa-times' />
                  </SearchIconWrapper>
              }
            </SearchWrapper>
        }
        {
          !this.expandSearch && isBlank(this.props.searchQuery) && isPresent(this.props.filteredTagCategories) &&
            <div style={{ position: 'relative' }}>
              <FilterWrapper>
                {
                  this.hasFilterTags && !AppUI.layout.isMobile &&
                    <WorksheetFilterTags
                      isExerciseFilter={this.props.isExerciseFilter}
                      onUpdateFilter={this.props.onUpdateFilter}
                      tagCategories={this.userGroupFilteredTagCategories}
                      showProgressTag={this.props.showProgressTag}
                      activeView={this.props.activeView}
                    />
                }
                <FilterIconWrapper
                  onClick={() => this.showFilterOptions = true}
                >
                  <FilterIcon
                    width={20}
                    height={20}
                    fill='#444'
                  />
                </FilterIconWrapper>
              </FilterWrapper>
              {
                AppUI.layout.isMobile && this.activeFilterCount > 0 &&
                  <FilterCountBubble>
                    {this.activeFilterCount}
                  </FilterCountBubble>
              }
            </div>
        }
        {
          !this.expandSearch && this.props.onToggleSort &&
            <SortButton
              onClick={() => this.props.onToggleSort('worksheets.published_at')}
              disabled={this.expandSearch}
              background={AppUI.layout.isMobile ? '#e8e8e8' : '#FFF'}
            >
              <i className={`fa ${this.props.sortDirection === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up'}`} />
              Sort by Date
            </SortButton>
        }
        {
          !this.expandSearch && AppUI.site.showExerciseView && isPresent(this.props.showExerciseView) && isFunction(this.props.onToggleExerciseView) &&
            <SegmentedControl
              options={[
                { value: 'videos', label: 'Posts' },
                { value: 'exercises', label: 'Exercises' },
              ]}
              activeOption={this.props.showExerciseView ? 'exercises' : 'videos'}
              onChange={this.props.onToggleExerciseView}
              fontSize='12px'
              buttonWidth={AppUI.layout.isMobile ? 'none' : '96px'}
            />
        }
      </Wrapper>
    );
  }
}

export default WorksheetFilter;
